import { useEffect, useState, type RefObject } from "react";
import { throttleRAF } from "@/utils/utils";

interface UseScrollVisibilityOptions {
  /**
   * 是否啟用滾動監聽
   * @default true
   */
  enabled?: boolean;
}

/**
 * 當頁面向下滾動超過指定 ref 元件的底部位置時，返回 true
 *
 * @param ref - 要監聽的 DOM 元素 ref
 * @param options - 配置選項
 * @returns show 狀態，當滾動位置超過 ref 元件的底部時為 true
 *
 * @example
 * const elementRef = useRef<HTMLDivElement>(null);
 * const showFixedBtn = useScrollVisibility(elementRef, { enabled: !isDesktop });
 *
 * return (
 *   <>
 *     <div ref={elementRef}>Content</div>
 *     {showFixedBtn && <FixedButton />}
 *   </>
 * );
 */
const useScrollVisibility = <T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  options: UseScrollVisibilityOptions = {},
): boolean => {
  const { enabled = true } = options;
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!enabled || !ref.current) {
      return;
    }

    const handleScroll = () => {
      if (!ref.current) {
        return;
      }

      // 獲取元素底部相對於視窗頂部的距離
      // getBoundingClientRect().bottom 返回元素底部到視窗頂部的距離
      // - 正值：元素底部在視窗頂部下方（元素可見或部分可見）
      // - 負值：元素底部在視窗頂部上方（元素已完全滾動過，不可見）
      // 當 elementRectBottom < 0 時，表示元素底部已滾動到視窗頂部上方，應顯示固定按鈕
      const elementRectBottom = ref.current.getBoundingClientRect().bottom;
      setShow(elementRectBottom < 0);
    };

    handleScroll();

    const throttledHandleScroll = throttleRAF(handleScroll);

    // passive: true 告訴瀏覽器此事件監聽器不會呼叫 preventDefault()
    // 瀏覽器可以立即執行滾動，無需等待事件處理完成，提升滾動流暢度，可優化滾動效能
    window.addEventListener("scroll", throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
    };
  }, [enabled, ref]);

  return show;
};

export default useScrollVisibility;
